<template>
    <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
            <div>
                <div class="d-flex align-items-center gap-2">
                    <h2 class="h6 mb-0">Client Migration Analysis (FROM/TO)</h2>
                    <span v-if="dateRange" class="badge bg-primary">
                        <i class="fas fa-calendar-alt"></i> {{ dateRange }}
                    </span>
                </div>
                <small class="text-muted">Track where clients come from and where they go</small>
            </div>
            <div class="d-flex gap-2">
                <button class="btn btn-sm" :class="filter === 'all' ? 'btn-primary' : 'btn-outline-primary'" @click="filter = 'all'">
                    All ({{ normalizedFrotoData.length }})
                </button>
                <button class="btn btn-sm" :class="filter === 'gains' ? 'btn-success' : 'btn-outline-success'" @click="filter = 'gains'">
                    Gains ({{ gainsCount }})
                </button>
                <button class="btn btn-sm" :class="filter === 'losses' ? 'btn-danger' : 'btn-outline-danger'" @click="filter = 'losses'">
                    Losses ({{ lossesCount }})
                </button>
            </div>
        </div>
        <div class="card-body">
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading migration data...</span>
                </div>
            </div>

            <div v-else-if="normalizedFrotoData.length === 0" class="text-center text-muted py-5">
                <i class="fas fa-exchange-alt fa-3x mb-3 opacity-25"></i>
                <p>No migration data available for the selected period</p>
            </div>

            <div v-else>
                <div class="table-container">
                    <table class="table table-hover table-sticky-header">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Municipality</th>
                                <th>State</th>
                                <th>Population</th>
                                <th>FROM Platform</th>
                                <th>TO Platform</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in filteredData" :key="index">
                                <td>
                                    <span class="badge" :class="item.type === 'gain' ? 'bg-success' : 'bg-danger'">
                                        <i class="fas" :class="item.type === 'gain' ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                                        {{ item.type === 'gain' ? 'Gain' : 'Loss' }}
                                    </span>
                                </td>
                                <td>
                                    <a 
                                        v-if="item.municipality_id" 
                                        href="#" 
                                        @click.prevent="navigateToMunicipality(item.municipality_id)"
                                        class="text-decoration-none fw-bold municipality-link"
                                    >
                                        {{ item.municipality_name }}
                                        <i class="fas fa-external-link-alt ms-1 small"></i>
                                    </a>
                                    <strong v-else>{{ item.municipality_name }}</strong>
                                </td>
                                <td>{{ item.state }}</td>
                                <td>{{ formatNumber(item.population) }}</td>
                                <td>
                                    <span v-if="item.from_platform" class="badge bg-secondary">{{ item.from_platform }}</span>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>
                                    <span v-if="item.to_platform" class="badge bg-secondary">{{ item.to_platform }}</span>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>{{ formatDate(item.date) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Summary Stats -->
                <div class="row mt-3">
                    <div class="col-md-6">
                        <div class="card bg-light">
                            <div class="card-body">
                                <h6 class="text-success mb-2"><i class="fas fa-arrow-up"></i> Gains Summary</h6>
                                <div class="small">
                                    <strong>{{ gainsCount }}</strong> new clients
                                    <span class="text-muted ms-2">Avg Pop: {{ formatNumber(avgGainsPopulation) }}</span>
                                </div>
                                <div class="small mt-1">Top sources: {{ topSources }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="card bg-light">
                            <div class="card-body">
                                <h6 class="text-danger mb-2"><i class="fas fa-arrow-down"></i> Losses Summary</h6>
                                <div class="small">
                                    <strong>{{ lossesCount }}</strong> lost clients
                                    <span class="text-muted ms-2">Avg Pop: {{ formatNumber(avgLossesPopulation) }}</span>
                                </div>
                                <div class="small mt-1">Top destinations: {{ topDestinations }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format } from 'date-fns'

export default {
    name: 'FrotoMigrationTable',
    props: {
        frotoData: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        dateRange: { type: String, default: null }
    },
    data() {
        return {
            filter: 'all'
        }
    },
    computed: {
        normalizedFrotoData() {
            // Handle various data structures
            if (!this.frotoData) return []
            if (Array.isArray(this.frotoData)) return this.frotoData
            // If it's an object, try common property names
            if (this.frotoData.data && Array.isArray(this.frotoData.data)) return this.frotoData.data
            if (this.frotoData.items && Array.isArray(this.frotoData.items)) return this.frotoData.items
            return []
        },
        filteredData() {
            if (this.filter === 'gains') return this.normalizedFrotoData.filter(item => item.type === 'gain')
            if (this.filter === 'losses') return this.normalizedFrotoData.filter(item => item.type === 'loss')
            return this.normalizedFrotoData
        },
        gainsCount() {
            return this.normalizedFrotoData.filter(item => item.type === 'gain').length
        },
        lossesCount() {
            return this.normalizedFrotoData.filter(item => item.type === 'loss').length
        },
        avgGainsPopulation() {
            const gains = this.normalizedFrotoData.filter(item => item.type === 'gain')
            if (gains.length === 0) return 0
            return Math.round(gains.reduce((acc, item) => acc + (item.population || 0), 0) / gains.length)
        },
        avgLossesPopulation() {
            const losses = this.normalizedFrotoData.filter(item => item.type === 'loss')
            if (losses.length === 0) return 0
            return Math.round(losses.reduce((acc, item) => acc + (item.population || 0), 0) / losses.length)
        },
        topSources() {
            const gains = this.normalizedFrotoData.filter(item => item.type === 'gain')
            const sources = {}
            gains.forEach(item => {
                if (item.from_platform) sources[item.from_platform] = (sources[item.from_platform] || 0) + 1
            })
            const sorted = Object.entries(sources).sort((a, b) => b[1] - a[1]).slice(0, 3)
            return sorted.map(([platform, count]) => `${platform} (${count})`).join(', ') || 'None'
        },
        topDestinations() {
            const losses = this.normalizedFrotoData.filter(item => item.type === 'loss')
            const destinations = {}
            losses.forEach(item => {
                if (item.to_platform) destinations[item.to_platform] = (destinations[item.to_platform] || 0) + 1
            })
            const sorted = Object.entries(destinations).sort((a, b) => b[1] - a[1]).slice(0, 3)
            return sorted.map(([platform, count]) => `${platform} (${count})`).join(', ') || 'None'
        }
    },
    methods: {
        formatNumber(num) {
            if (!num && num !== 0) return '-'
            return num.toLocaleString()
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            return format(new Date(dateString), 'MMM d, yyyy')
        },
        navigateToMunicipality(municipalityId) {
            if (municipalityId) {
                const url = this.$router.resolve(`/municipalities/edit/${municipalityId}`).href
                window.open(url, '_blank')
            }
        }
    }
}
</script>

<style scoped>
.table-container {
    max-height: 500px;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.table-sticky-header {
    margin-bottom: 0;
}

.table-sticky-header thead th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 10;
    border-bottom: 2px solid #dee2e6;
}

.table-sticky-header tbody tr:hover {
    background-color: #f8f9fa;
}

.municipality-link {
    color: #0d6efd;
    transition: color 0.2s ease;
}

.municipality-link:hover {
    color: #0a58ca;
    text-decoration: underline !important;
}
</style>
